import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGetAirportsBySearchParamQuery, useGetDefaultAirportSuggestionsQuery, } from '@massilia/queries';
import { AutoComplete, Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, FullScreenOverlay, Skeleton, useDebounce, useIsMobile, } from '@massilia/ui';
import { cn } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { Check, MapPin, PlaneLanding, PlaneTakeoff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { DEFAULT_OPTION } from '../schemas/estimate/base-schemas.js';
import { FieldError } from './field-error';
const cityOption = (cityGroup) => ({
    category: 'city',
    icon: _jsx(MapPin, { className: "h-4 w-4 text-gray-500" }),
    key: `city-${cityGroup.iataCityCode}`,
    label: `${cityGroup.cityName} (Tous)`,
    value: cityGroup.iataCityCode,
});
/** Ville + ses aéroports en enfants : affichage des résultats de recherche. */
const buildSearchOptions = (response, mode) => {
    if (!response)
        return [];
    const options = [];
    response.groupedByCitiesAirports.forEach((cityGroup) => {
        options.push(cityOption(cityGroup));
        cityGroup.airports.forEach((airport, index) => {
            if (airport.iataCode) {
                const isLastChild = index === cityGroup.airports.length - 1;
                options.push({
                    category: 'airport',
                    icon: mode === 'departure' ? (_jsx(PlaneTakeoff, { className: "h-4 w-4 text-gray-500" })) : (_jsx(PlaneLanding, { className: "h-4 w-4 text-gray-500" })),
                    isChild: true,
                    isLastChild,
                    key: `airport-${airport.iataCode}`,
                    label: `${cityGroup.cityName} ${airport.name} (${airport.iataCode})`,
                    value: airport.iataCode,
                });
            }
        });
    });
    return options;
};
/** Une ligne par ville, sans expansion des aéroports : suggestions par défaut. */
const buildDefaultOptions = (response) => response ? response.groupedByCitiesAirports.map(cityOption) : [];
export function AirportAutocompleteField({ triggerClassName, autoFocus, errorPopoverSide = 'bottom', errorsDisplayType = 'popover', label, mode, size = 'default', }) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 200);
    const isEmptySearch = debouncedSearchText.trim().length === 0;
    const { data: searchedOptions, isFetching: isSearching } = useGetAirportsBySearchParamQuery({
        enabled: !isEmptySearch,
        searchParam: debouncedSearchText,
    });
    const { data: defaultSuggestions, isPending: isLoadingDefaults } = useGetDefaultAirportSuggestionsQuery({ mode });
    const options = useMemo(() => isEmptySearch
        ? buildDefaultOptions(defaultSuggestions)
        : buildSearchOptions(searchedOptions, mode), [isEmptySearch, defaultSuggestions, searchedOptions, mode]);
    const isLoadingOptions = isEmptySearch ? isLoadingDefaults : isSearching;
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const isMobile = useIsMobile();
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const selectedOption = useMemo(() => {
        const currentSelection = field.state.value;
        if (!currentSelection?.value)
            return undefined;
        // Le libellé stocké fait foi : une même valeur peut exister en ville et en
        // aéroport (ex. MRS), et la liste affichée n'est pas forcément celle d'origine.
        if (currentSelection.label)
            return currentSelection;
        return options.find((opt) => opt.value === currentSelection.value);
    }, [options, field.state.value]);
    const triggerIcon = mode === 'departure' ? (_jsx(PlaneTakeoff, { className: "text-gray-light-700 h-4 w-4 shrink-0" })) : (_jsx(PlaneLanding, { className: "text-gray-light-700 h-4 w-4 shrink-0" }));
    const placeholder = mode === 'departure' ? 'Lieu de départ' : 'Destination';
    // Mobile: full-page search overlay instead of a portal dropdown that the
    // keyboard would otherwise cover.
    if (isMobile) {
        const handleSelect = (option) => {
            field.handleChange({ category: option.category, label: option.label, value: option.value });
            setSearchText('');
            setIsOverlayOpen(false);
        };
        return (_jsxs("div", { className: "relative flex flex-1 flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), errorsDisplayType === 'popover' && (_jsx(FieldError, { asPopover: true, asPopoverSide: errorPopoverSide, errors: errors })), _jsxs(Button, { "aria-invalid": errors.length > 0, className: cn('data-[empty=true]:text-muted-foreground relative w-full justify-start gap-2 p-4 text-left font-normal', 'aria-invalid:ring-destructive/20 aria-invalid:border-destructive', triggerClassName), "data-empty": !selectedOption, onClick: () => setIsOverlayOpen(true), type: "button", variant: "outline", children: [triggerIcon, _jsx("span", { className: "text-gray-light-600 truncate", children: selectedOption?.label ?? placeholder })] }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors }), _jsx(FullScreenOverlay, { onOpenAutoFocus: (e) => {
                        // Focus the search input ourselves: Radix's focus scope relies on
                        // document.activeElement, which breaks across the widget's shadow-DOM
                        // boundary, so the keyboard wouldn't open there.
                        e.preventDefault();
                        e.currentTarget
                            ?.querySelector('[data-slot="command-input"]')
                            ?.focus();
                    }, onOpenChange: (open) => {
                        setIsOverlayOpen(open);
                        if (!open) {
                            // Rouvrir le champ repart d'une recherche vide → suggestions par défaut.
                            setSearchText('');
                            field.handleBlur();
                        }
                    }, open: isOverlayOpen, title: placeholder, children: _jsxs(Command, { className: "flex h-full flex-col rounded-none", shouldFilter: false, children: [_jsx("div", { className: "border-b px-4 py-3", children: _jsx(CommandInput, { autoFocus: true, icon: triggerIcon, onValueChange: setSearchText, placeholder: placeholder, value: searchText }) }), _jsx(CommandList, { className: "max-h-none flex-1 px-2 py-2", children: options.length > 0 ? (_jsx(CommandGroup, { children: options.map((option) => {
                                        const isSelected = selectedOption?.value === option.value;
                                        return (_jsxs(CommandItem, { className: cn('flex w-full cursor-pointer items-center gap-3 px-2 py-3', option.isChild && 'airport-child', option.isChild && option.isLastChild && 'airport-child-last'), onSelect: () => handleSelect(option), value: option.label, children: [_jsx("div", { className: "flex w-5 justify-center", children: option.icon }), _jsx("span", { className: "flex-1 text-left", children: option.label }), isSelected && _jsx(Check, { className: "text-brand-600 h-4 w-4 shrink-0" })] }, option.key ?? option.value));
                                    }) })) : isLoadingOptions ? (_jsxs("div", { className: "flex flex-col gap-2 px-2 py-2", children: [_jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full" })] })) : (_jsx(CommandEmpty, { children: "Aucun r\u00E9sultat trouv\u00E9" })) })] }) })] }));
    }
    return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), errorsDisplayType === 'popover' && (_jsx(FieldError, { asPopover: true, asPopoverSide: errorPopoverSide, errors: errors })), _jsx(AutoComplete, { autoFocus: autoFocus, className: cn('flex-1', { 'w-74': !isMobile && size === 'compact' }), emptyMessage: "Aucun r\u00E9sultat trouv\u00E9", icon: mode === 'departure' ? (_jsx(PlaneTakeoff, { className: "text-gray-light-700 h-4 w-4" })) : (_jsx(PlaneLanding, { className: "text-gray-light-700 h-4 w-4" })), isLoading: isLoadingOptions && options.length === 0, onBlurCallBack: () => {
                    // Rouvrir le champ repart d'une recherche vide → suggestions par défaut.
                    setSearchText('');
                    field.handleBlur();
                }, onInputChange: (text) => {
                    setSearchText(text);
                }, onOpenChange: (open) => {
                    // Clear the validation error popover so it doesn't overlap the dropdown.
                    // setErrorMap merges, so each source must be set to undefined explicitly.
                    if (open) {
                        field.setErrorMap({
                            onBlur: undefined,
                            onChange: undefined,
                            onMount: undefined,
                            onSubmit: undefined,
                        });
                    }
                }, onValueChange: (opt) => {
                    setSearchText('');
                    if (opt) {
                        // Only keep serializable data fields, strip rendering metadata (icon, isChild, etc.)
                        field.handleChange({ category: opt.category, label: opt.label, value: opt.value });
                    }
                    else {
                        field.handleChange(DEFAULT_OPTION);
                    }
                }, options: options, placeholder: placeholder, shouldFilter: false, value: selectedOption }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors })] }));
}
